import type { SaleFinancialSummary } from '@klothiq/domain';
import { supabase } from '@/lib/supabase';
import { salesError } from '@/lib/salesOverview';

export interface SelectedSaleItem {
  id: string;
  name: string;
  sku: string | null;
  size: string | null;
  color: string | null;
  quantity: number;
  returnedQuantity: number;
  unitPrice: number;
  discount: number;
  total: number;
}

export interface SelectedSaleDocument {
  id: string;
  number: string;
  date: string;
  updatedAt: string;
  customerName: string | null;
  phone: string | null;
  address: string | null;
  pincode: string | null;
  seller: string | null;
  orderStatus: string;
  paymentStatus: string;
  returnStatus: string | null;
  deliveryCharge: number;
  total: number;
  financial: SaleFinancialSummary;
  items: SelectedSaleItem[];
}

// No cost, profit, account, payment-reference or internal-note fields are requested.
const detailProjection = `
  id,organization_id,sale_number,sale_date,updated_at,
  customer_name,customer_mobile,customer_address,seller_name,
  sale_status,payment_status,return_status,delivery_charge,total_amount,is_voided,is_deleted,
  customers(name,phone,address,city,pincode),
  sale_items(id,quantity,returned_qty,unit_price,discount,line_total,
    product_variants(sku,size,color,products(name)))
`;

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function number(value: unknown): number {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value)))
    throw new Error('Saved sale amounts are incomplete. Refresh the sale before exporting.');
  return Number(value);
}

function aborted(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Preparation cancelled.', 'AbortError');
}

function relation<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) {
    if (value.length > 1) throw new Error('Saved sale links are ambiguous. Refresh the sale.');
    return value[0] ?? null;
  }
  return value ?? null;
}

async function readSelectedSale(id: string, organizationId: string, signal: AbortSignal) {
  for (let attempt = 0; attempt < 2; attempt++) {
    aborted(signal);
    const detail = await supabase
      .from('sales')
      .select(detailProjection)
      .eq('id', id)
      .eq('organization_id', organizationId)
      .abortSignal(signal)
      .single();
    aborted(signal);
    if (detail.error) throw new Error(salesError(detail.error));
    const sale = detail.data;
    if (!sale || sale.id !== id || sale.is_deleted || sale.is_voided)
      throw new Error('This sale is no longer available. Refresh the list.');
    const overview = await supabase
      .rpc('get_sale_overview_row', { p_sale_id: id })
      .abortSignal(signal);
    aborted(signal);
    if (overview.error) throw new Error(salesError(overview.error));
    const current = overview.data;
    if (!current || current.id !== id)
      throw new Error('This sale is no longer available. Refresh the list.');
    if (current.updated_at && Date.parse(current.updated_at) !== Date.parse(sale.updated_at)) {
      if (attempt === 0) continue;
      throw new Error('The sale changed while preparing. Refresh and try again.');
    }
    const financial = current.financial_summary;
    if (!financial || typeof financial.return_review !== 'boolean')
      throw new Error('Current payment details are unavailable. Refresh and try again.');
    const customer = relation(sale.customers);
    const snapshotAddress = text(sale.customer_address);
    const address =
      snapshotAddress ||
      [
        ...new Set(
          [text(customer?.address), text(customer?.city), text(customer?.pincode)].filter(Boolean),
        ),
      ].join(', ') ||
      null;
    // Build an explicit output DTO; never spread the financial overview response.
    const result: SelectedSaleDocument = {
      id: sale.id,
      number: sale.sale_number,
      date: sale.sale_date,
      updatedAt: sale.updated_at,
      customerName: text(sale.customer_name) || text(customer?.name),
      phone: text(sale.customer_mobile) || text(customer?.phone),
      address,
      pincode: snapshotAddress ? null : text(customer?.pincode),
      seller: text(current.seller_name) || text(sale.seller_name),
      orderStatus: current.sale_status,
      paymentStatus: current.payment_status,
      returnStatus: text(current.return_status),
      deliveryCharge: number(sale.delivery_charge),
      total: number(sale.total_amount),
      financial: {
        collected: number(financial.collected),
        refunded: number(financial.refunded),
        net_collected: number(financial.net_collected),
        amount_due: financial.amount_due === null ? null : number(financial.amount_due),
        return_review: financial.return_review,
      },
      items: (sale.sale_items ?? []).map((item) => {
        const variant = relation(item.product_variants);
        const product = relation(variant?.products);
        return {
          id: item.id,
          name: text(product?.name) || 'Product name not recorded',
          sku: text(variant?.sku),
          size: text(variant?.size),
          color: text(variant?.color),
          quantity: number(item.quantity),
          returnedQuantity: number(item.returned_qty),
          unitPrice: number(item.unit_price),
          discount: number(item.discount),
          total: number(item.line_total),
        };
      }),
    };
    return result;
  }
  throw new Error('The sale changed. Please try again.');
}

/** Read only the frozen selection; never return a partial result on failure. */
export async function loadSelectedSales(
  selection: ReadonlyArray<{ id: string; sale_number: string }>,
  organizationId: string,
  signal: AbortSignal,
  onProgress: (done: number, total: number) => void,
): Promise<SelectedSaleDocument[]> {
  if (!selection.length) throw new Error('Select at least one sale.');
  if (!organizationId || new Set(selection.map((sale) => sale.id)).size !== selection.length)
    throw new Error('The selection changed. Please select the sales again.');
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal.addEventListener('abort', cancel, { once: true });
  if (signal.aborted) controller.abort();
  const results: SelectedSaleDocument[] = new Array(selection.length);
  let next = 0;
  let done = 0;
  onProgress(0, selection.length);
  try {
    await Promise.all(
      Array.from({ length: Math.min(4, selection.length) }, async () => {
        while (next < selection.length) {
          aborted(controller.signal);
          const index = next++;
          const sale = selection[index]!;
          try {
            results[index] = await readSelectedSale(sale.id, organizationId, controller.signal);
          } catch (error) {
            if (controller.signal.aborted) throw error;
            throw new Error(`${sale.sale_number}: ${salesError(error)}`);
          }
          aborted(controller.signal);
          onProgress(++done, selection.length);
        }
      }),
    );
    aborted(controller.signal);
    return results;
  } catch (error) {
    controller.abort();
    throw error;
  } finally {
    signal.removeEventListener('abort', cancel);
  }
}
